import React, { useState, useEffect } from "react";
import { X, Calendar, Send, AlertCircle, DollarSign } from "lucide-react";
import { API_BASE_URL } from "../../utils/apiConfig";

interface PickupDateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (pickupDate: string, notes?: string, priceRounding?: Record<number, boolean>) => void | Promise<void>;
  orderId: number;
  shippingCompany: string;
  isBulk?: boolean;
  bulkCount?: number;
  orders?: Array<{ id: number; price: number; currency: string; source?: string }>;
}

interface IssuedPickupList {
  pickupDate: string;
  pickupListNo: string;
  issuedAt: string;
}

const PickupDateModal: React.FC<PickupDateModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  orderId,
  shippingCompany,
  isBulk = false,
  bulkCount = 0,
  orders = [],
}) => {
  const [pickupDate, setPickupDate] = useState("");
  const [notes, setNotes] = useState("");
  const [showNotesField, setShowNotesField] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [issuedPickupLists, setIssuedPickupLists] = useState<
    IssuedPickupList[]
  >([]);
  const [loadingPickupLists, setLoadingPickupLists] = useState(false);
  
  // Price rounding state - maps order ID to whether rounding is enabled
  const [priceRounding, setPriceRounding] = useState<Record<number, boolean>>({});

  // Helper function to check if a price needs rounding (has any decimal part)
  const needsRounding = (price: number): boolean => {
    const decimal = price % 1;
    // Check if price has any cents (decimals)
    return decimal > 0;
  };

  // Get the rounded price for an order (round to nearest whole number)
  const getRoundedPrice = (price: number): number => {
    return Math.round(price);
  };

  // Get display price (original or rounded based on toggle)
  const getDisplayPrice = (order: { id: number; price: number; currency: string }): { price: number; isRounded: boolean } => {
    if (priceRounding[order.id]) {
      return { price: getRoundedPrice(order.price), isRounded: true };
    }
    return { price: order.price, isRounded: false };
  };

  // Get orders that need rounding (exclude web orders)
  const ordersNeedingRounding = orders.filter(order => 
    needsRounding(order.price) && order.source !== 'web'
  );

  // Group orders by price for display
  const groupedOrders = React.useMemo(() => {
    const groups = new Map<string, { 
      price: number; 
      currency: string; 
      orders: Array<{ id: number; price: number; currency: string; source?: string }> 
    }>();
    
    ordersNeedingRounding.forEach(order => {
      const key = `${order.price.toFixed(2)}_${order.currency}`;
      if (!groups.has(key)) {
        groups.set(key, {
          price: order.price,
          currency: order.currency,
          orders: []
        });
      }
      groups.get(key)!.orders.push(order);
    });
    
    return Array.from(groups.values());
  }, [ordersNeedingRounding]);

  // Get today's date as default
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  // Get max allowed date (7 days from today)
  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 7);
    return maxDate.toISOString().split("T")[0];
  };

  // Check if a date is Sunday
  const isSunday = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.getDay() === 0;
  };

  // Load issued pickup lists when modal opens
  useEffect(() => {
    if (
      isOpen &&
      (shippingCompany === "acs" || shippingCompany === "easymail")
    ) {
      loadIssuedPickupLists();
    }
  }, [isOpen, shippingCompany]);

  // Load issued pickup lists for the shipping company
  const loadIssuedPickupLists = async () => {
    setLoadingPickupLists(true);
    try {
      // Get the date range (today + next 7 days)
      const today = new Date();
      const maxDate = new Date();
      maxDate.setDate(maxDate.getDate() + 7);

      const fromDate = today.toISOString().split("T")[0];
      const toDate = maxDate.toISOString().split("T")[0];

      const response = await fetch(
        `${API_BASE_URL}/api/shipping/${shippingCompany}/pickup-lists?fromDate=${fromDate}&toDate=${toDate}&status=issued`
      );

      const result = await response.json();

      if (result.success && result.data) {
        setIssuedPickupLists(result.data.pickupLists || []);
        console.log(
          `📋 [PICKUP DATE] Loaded ${
            result.data.pickupLists?.length || 0
          } issued pickup lists`
        );
      }
    } catch (error) {
      console.error("Error loading issued pickup lists:", error);
    } finally {
      setLoadingPickupLists(false);
    }
  };

  // Check if a date has an issued pickup list
  const hasIssuedPickupList = (dateStr: string) => {
    return issuedPickupLists.some((list) => list.pickupDate === dateStr);
  };

  // Initialize with today's date and check for issued pickup list
  useEffect(() => {
    if (isOpen) {
      const todayDate = getTodayDate();
      setNotes("");
      setShowNotesField(false);
      setError(null);
      setIsProcessing(false);
      
      // Reset price rounding state
      setPriceRounding({});

      // Check if today is Sunday
      if (isSunday(todayDate)) {
        setPickupDate("");
        setError("Today is Sunday. Please select a different pickup date (courier does not operate on Sundays).");
        return;
      }

      // Set today as default pickup date
      setPickupDate(todayDate);

      // Check if today's date has an issued pickup list
      if (hasIssuedPickupList(todayDate)) {
        const issuedList = issuedPickupLists.find(
          (list) => list.pickupDate === todayDate
        );
        setError(
          `Pickup list already issued for this date (${
            issuedList?.pickupListNo || "Unknown"
          }). Please select a different date.`
        );
      }
    }
  }, [isOpen, issuedPickupLists]);

  // Check if selected date is different from today
  useEffect(() => {
    const todayDate = getTodayDate();
    setShowNotesField(pickupDate !== todayDate && pickupDate !== "");
  }, [pickupDate]);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    // Validate date
    if (!pickupDate) {
      setError("Please select a pickup date");
      return;
    }

    // Check if pickup list already issued for this date
    if (hasIssuedPickupList(pickupDate)) {
      const issuedList = issuedPickupLists.find(
        (list) => list.pickupDate === pickupDate
      );
      setError(
        `Pickup list already issued for this date.\n\nPickup List: ${
          issuedList?.pickupListNo || "Unknown"
        }\nIssued: ${
          issuedList?.issuedAt
            ? new Date(issuedList.issuedAt).toLocaleString()
            : "Unknown"
        }\n\nPlease select a different date.`
      );
      return;
    }

    // Validate notes if date is not today
    if (showNotesField && !notes.trim()) {
      setError("Please provide a reason for choosing a different pickup date");
      return;
    }

    // Validate date is not in the past
    const selectedDate = new Date(pickupDate);
    selectedDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      setError("Pickup date cannot be in the past");
      return;
    }

    // Validate date is not too far in the future (max 7 days)
    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + 7);
    if (selectedDate > maxDate) {
      setError("Pickup date cannot be more than 7 days in the future");
      return;
    }

    // Validate date is not a Sunday
    const dayOfWeek = selectedDate.getDay();
    if (dayOfWeek === 0) {
      setError("Pickup date cannot be on Sunday (courier does not operate)");
      return;
    }

    // Show loading state and wait for completion
    setIsProcessing(true);
    try {
      await onConfirm(pickupDate, notes.trim() || undefined, priceRounding);
    } catch (error) {
      console.error("Error processing order(s):", error);
      setError("Failed to process order(s). Please try again.");
      setIsProcessing(false);
    }
    // Note: isProcessing will be reset when modal closes
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const todayDate = getTodayDate();
  const isDifferentDate = pickupDate !== todayDate;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Calendar className="w-5 h-5 mr-2 text-[#264a3e]" />
            {isBulk
              ? `Bulk Shipping - ${bulkCount} Orders`
              : "Select Pickup Date"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          {/* Order Info */}
          <div
            className="mb-4 p-3 border rounded-lg"
            style={{ backgroundColor: "#f0f9f4", borderColor: "#264a3e" }}
          >
            <p className="text-sm" style={{ color: "#264a3e" }}>
              {isBulk ? (
                <>
                  <strong>Bulk Shipping:</strong> {bulkCount} Orders
                  <br />
                  <strong>Shipping Company:</strong>{" "}
                  {shippingCompany.toUpperCase()}
                  <br />
                  <span className="text-xs">
                    All orders will use the same pickup date
                  </span>
                </>
              ) : (
                <>
                  <strong>Order:</strong> #{orderId}
                  <br />
                  <strong>Shipping Company:</strong>{" "}
                  {shippingCompany.toUpperCase()}
                </>
              )}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-800">{error}</div>
            </div>
          )}

          {/* Price Rounding Section */}
          {groupedOrders.length > 0 && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center mb-3">
                <DollarSign className="w-5 h-5 text-blue-600 mr-2" />
                <h4 className="text-sm font-medium text-blue-900">
                  Price Rounding Options
                </h4>
              </div>
              <p className="text-xs text-blue-700 mb-3">
                Some orders have prices with cents. You can round them up to whole numbers before shipping.
              </p>
              <div className="space-y-2">
                {groupedOrders.map((group) => {
                  const roundedPrice = getRoundedPrice(group.price);
                  const allRounded = group.orders.every(order => priceRounding[order.id]);
                  
                  return (
                    <div key={`${group.price}_${group.currency}`} className="flex items-center justify-between p-3 bg-white rounded border border-blue-200">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          {group.orders.length} order{group.orders.length > 1 ? 's' : ''} at {group.price.toFixed(2)} {group.currency}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          AlterCPA: {group.price.toFixed(2)} {group.currency}
                          {allRounded && (
                            <span className="text-green-600 ml-2">
                              → {roundedPrice.toFixed(2)} {group.currency} ✓
                            </span>
                          )}
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer ml-4">
                        <input
                          type="checkbox"
                          checked={allRounded}
                          onChange={(e) => {
                            const newRounding: Record<number, boolean> = {};
                            group.orders.forEach(order => {
                              newRounding[order.id] = e.target.checked;
                            });
                            setPriceRounding(prev => ({
                              ...prev,
                              ...newRounding
                            }));
                          }}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Date Picker */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Pickup Date
            </label>
            <input
              type="date"
              value={pickupDate}
              onChange={(e) => {
                const newDate = e.target.value;
                setPickupDate(newDate);
                setError(null);

                // Check if pickup list already issued for this date
                if (hasIssuedPickupList(newDate)) {
                  const issuedList = issuedPickupLists.find(
                    (list) => list.pickupDate === newDate
                  );
                  setError(
                    `Pickup list already issued for this date (${
                      issuedList?.pickupListNo || "Unknown"
                    }). Please select a different date.`
                  );
                }
                // Show warning if Sunday is selected
                else if (isSunday(newDate)) {
                  setError("Warning: Sundays are not allowed for pickup");
                }
              }}
              min={getTodayDate()}
              max={getMaxDate()}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e]"
            />
            {pickupDate && (
              <p className="mt-2 text-sm text-gray-600">
                {isDifferentDate ? (
                  <span
                    className={
                      isSunday(pickupDate)
                        ? "text-red-600 font-medium"
                        : "text-orange-600 font-medium"
                    }
                  >
                    Selected: {formatDate(pickupDate)}
                    {isSunday(pickupDate) && " Sunday (Not Allowed)"}
                  </span>
                ) : (
                  <span className="text-green-600 font-medium">
                    Default: {formatDate(pickupDate)} (Today)
                  </span>
                )}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Maximum 7 days from today. Sundays not allowed.
            </p>
          </div>

          {/* Notes field - shown when date is not tomorrow */}
          {showNotesField && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Different Pickup Date *
              </label>
              <textarea
                value={notes}
                onChange={(e) => {
                  setNotes(e.target.value);
                  setError(null);
                }}
                rows={3}
                placeholder="Please explain why you need a different pickup date..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e]"
              />
              <p className="mt-1 text-xs text-gray-500">
                This field is required when selecting a other date
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-4 border-t border-gray-200 space-x-3">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={Boolean(isProcessing || !pickupDate || (pickupDate && (hasIssuedPickupList(pickupDate) || isSunday(pickupDate))))}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {isBulk ? `Processing ${bulkCount} Orders...` : "Processing..."}
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                {isBulk ? `Ship ${bulkCount} Orders` : "Confirm & Ship"}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PickupDateModal;
